import isEqual from 'lodash/isEqual';
import type {PosterObject} from '@PosterWhiteboard/poster/poster.types';

/**
 * Structural sharing for the Redux poster mirror.
 *
 * Every commit serializes the whole poster (`Poster.toObject()`), which hands every page, item
 * and audio clip a brand-new object even when nothing about it changed. Redux, reselect and React
 * all compare by identity, so an unshared publish reads as "everything changed": every selector
 * recomputes, every memoized component re-renders and every thumbnail re-hashes, on every commit.
 *
 * This module runs between serialization and dispatch. For each item, audio clip and page it asks
 * "is this deeply equal to what the store already holds?" and, if so, puts the store's object back
 * into the fresh poster. Inside an item that DID change it does the same for every nested object
 * and array (textStyles, background, border, shadow, animations, colour arrays…): a padding
 * commit hands the font-size, styling, animation and list rows the SAME textStyles / animation /
 * lineStyles references they already hold, so only the rows whose value changed re-render (every
 * sibling row flashed on every slider step before 2026-10-12). After that, identity means
 * "unchanged since the last commit" all the way down the tree, which is what lets the timeline
 * bars, the thumbnail cache and the per-item selectors skip work.
 *
 * Rules the implementation relies on:
 * - `previous` is the object already in the store. Immer froze it; it is only ever read.
 * - `next` is the fresh serialization nobody else has seen yet, so it may be patched in place.
 * - Whole-page reuse is decided after item and audio reuse, so a page whose members all matched
 *   compares by identity for most of its weight and the deep comparison stays cheap.
 * - Cost is one deep comparison of the poster per commit, roughly the price of the serialization
 *   it follows. It never runs on playback ticks or drag ticks (see `posterSession`).
 */

/**
 * Returns `next` with every page, item and audio clip that is deeply equal to its counterpart in
 * `previous` replaced by that counterpart. On the first publish there is nothing to share, so
 * `next` is returned as is.
 */
export function stabilizePosterObject(previous: PosterObject | undefined, next: PosterObject): PosterObject {
  if (!previous) {
    return next;
  }

  for (const [pageId, page] of Object.entries(next.pages.pagesHashMap)) {
    const previousPage = previous.pages.pagesHashMap[pageId];
    if (previousPage === undefined) {
      continue;
    }
    shareUnchangedEntries(previousPage.items.itemsHashMap, page.items.itemsHashMap);
    shareUnchangedEntries(previousPage.audioClips.audioItemsHashMap, page.audioClips.audioItemsHashMap);
    if (isEqual(previousPage, page)) {
      next.pages.pagesHashMap[pageId] = previousPage;
    }
  }
  return next;
}

/**
 * Replaces entries of `next` (items or audio clips, keyed by uid) with the deeply equal entry from
 * `previous`; an entry that changed keeps its unchanged nested values instead.
 */
function shareUnchangedEntries<T>(previous: Record<string, T>, next: Record<string, T>): void {
  for (const [uid, entry] of Object.entries(next)) {
    const previousEntry = previous[uid];
    if (previousEntry === undefined) {
      continue;
    }
    if (isEqual(previousEntry, entry)) {
      next[uid] = previousEntry;
    } else {
      shareUnchangedNestedValues(previousEntry, entry);
    }
  }
}

type NestedContainer = Record<string, unknown> | unknown[];

const isNestedContainer = (value: unknown): value is NestedContainer => {
  return typeof value === 'object' && value !== null;
};

/**
 * Walks a changed value and puts the store's copy back for every nested object or array that is
 * deeply equal, recursing into the ones that are not. Only `next` is written; `previous` is the
 * frozen store object.
 */
function shareUnchangedNestedValues(previous: unknown, next: unknown): void {
  if (!isNestedContainer(previous) || !isNestedContainer(next) || Array.isArray(previous) !== Array.isArray(next)) {
    return;
  }
  const previousByKey = previous as Record<string, unknown>;
  const nextByKey = next as Record<string, unknown>;
  for (const key of Object.keys(nextByKey)) {
    const previousValue = previousByKey[key];
    const nextValue = nextByKey[key];
    if (!isNestedContainer(previousValue) || !isNestedContainer(nextValue)) {
      continue;
    }
    if (isEqual(previousValue, nextValue)) {
      nextByKey[key] = previousValue;
    } else {
      shareUnchangedNestedValues(previousValue, nextValue);
    }
  }
}
